import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, Panel, Badge, Select, Button, TextInput,
} from "../components/UIKit";
import { Play, FlaskConical, GitCompare, FileJson } from "lucide-react";

const DEFAULT_SESSION = {
  duration_min: 480,
  deep_min: 60,
  rem_min: 105,
  light_min: 280,
  awake_min: 35,
  hr_avg: 64,
  hr_min: 52,
  hr_max: 98,
  br_avg: 14.2,
  sleep_latency_min: 15,
  snore_min: 8,
  cough_per_hour: 0.4,
  breath_interruptions_per_hour: 0.7,
  ambient_db_median: 28,
  efficiency: 92,
};

export default function AdminSandbox() {
  const { api } = useAdminAuth();
  const [algos, setAlgos] = useState([]);
  const [algoA, setAlgoA] = useState("score_materasso");
  const [algoB, setAlgoB] = useState("sleep_staging");
  const [sessionText, setSessionText] = useState(
    JSON.stringify(DEFAULT_SESSION, null, 2)
  );
  const [sessionId, setSessionId] = useState("");
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api("/algorithms").then((d) => setAlgos(d.items || [])).catch(() => {});
  }, [api]);

  const parseSession = () => {
    if (sessionId) return { session_id: sessionId };
    return { session: JSON.parse(sessionText) };
  };

  const runOne = async (algoId, setter) => {
    setBusy(true);
    setError("");
    try {
      const payload = { algorithm_id: algoId, ...parseSession() };
      const res = await api("/sandbox/run", { method: "POST", data: payload });
      setter(res);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const compare = async () => {
    setBusy(true);
    setError("");
    try {
      const ses = parseSession();
      const res = await api("/sandbox/compare", {
        method: "POST",
        data: { algorithm_a: algoA, algorithm_b: algoB, ...ses },
      });
      setResultA(res.a);
      setResultB(res.b);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8">
      <PageHeader
        title="Sandbox / Testing"
        subtitle="Esegui algoritmi in modalità preview senza scrivere in produzione"
      />

      <div className="grid grid-cols-[1fr,1fr] gap-4">
        <Panel
          title="Input sessione"
          right={
            <span className="text-white/55 text-[11px] flex items-center gap-1">
              <FileJson size={12} /> JSON
            </span>
          }
        >
          <label className="text-white/60 text-[11px] uppercase tracking-wider">
            Session ID (opzionale, se vuoto usa JSON)
          </label>
          <div className="mt-1 mb-3">
            <TextInput
              placeholder="s_xxxxxxxxxx"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            />
          </div>
          <textarea
            value={sessionText}
            onChange={(e) => setSessionText(e.target.value)}
            rows={16}
            spellCheck={false}
            className="w-full rounded-lg bg-[#0a1317] border border-white/10 focus:border-[#14c2a1] text-[#c9fde9] text-[12.5px] font-mono p-3 focus:outline-none resize-y"
          />
        </Panel>

        <div className="space-y-4">
          <Panel
            title="Esecuzione comparativa A vs B"
            right={
              <Button onClick={compare} disabled={busy} size="sm">
                <GitCompare size={13} /> Confronta
              </Button>
            }
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/60 text-[11px] uppercase tracking-wider">
                  Algoritmo A
                </label>
                <div className="mt-1">
                  <Select
                    value={algoA}
                    onChange={setAlgoA}
                    options={algos.map((a) => ({
                      value: a.id,
                      label: `${a.name} (${a.current_version})`,
                    }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-white/60 text-[11px] uppercase tracking-wider">
                  Algoritmo B
                </label>
                <div className="mt-1">
                  <Select
                    value={algoB}
                    onChange={setAlgoB}
                    options={algos.map((a) => ({
                      value: a.id,
                      label: `${a.name} (${a.current_version})`,
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => runOne(algoA, setResultA)}
                disabled={busy}
              >
                <Play size={12} /> Run A
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => runOne(algoB, setResultB)}
                disabled={busy}
              >
                <Play size={12} /> Run B
              </Button>
              {error && (
                <span className="text-rose-300 text-[12px] self-center">{error}</span>
              )}
            </div>
          </Panel>

          <div className="grid grid-cols-2 gap-3">
            <Panel title="Risultato A">
              <pre className="text-[#c9fde9] text-[12px] font-mono whitespace-pre-wrap break-words max-h-[300px] overflow-auto">
                {resultA ? JSON.stringify(resultA, null, 2) : "Nessuna esecuzione"}
              </pre>
            </Panel>
            <Panel title="Risultato B">
              <pre className="text-[#c9fde9] text-[12px] font-mono whitespace-pre-wrap break-words max-h-[300px] overflow-auto">
                {resultB ? JSON.stringify(resultB, null, 2) : "Nessuna esecuzione"}
              </pre>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
